import { sum } from 'd3';

import { formattedDateTime } from '../../../../../utils/dateUtils';
import { slopePercentFromTriangle, vectorToSlopePercent } from '../../../../../utils/math/slope';
import { isDefined } from '../../../../../utils/variables';
import { ActionName, DesignTool } from '../../../../tools/design-tool';
import { SelectionResult } from '../../../../tools/utils/context-menu/design-tool/utils/select';
import { Design } from '../../../design';
import { AttributeData, AttributeId, getUnitType, SkyMapAttribute } from '../../skymap-extras';
import { DesignNode } from '../design-node';
import { findFaceIdFromIntersection } from './face-node';
import { findLineSegmentIdFromIntersection, getLineSegmentFaces } from './line-segment-node';
import { findPointIdFromIntersection } from './point-node';

export type DesignMetaData = {
  triangleCount: number;
  area: number;
  planarArea: number;
  lineLength: number;
  linePlanarLength: number;
  lineSegmentCount: number;
  pointCount: number;
};

export type DesignHoverMetadata = {
  attributes: [string, string][];
  data:
    | {
        type: 'line';
        length: number;
        planarLength: number;
        slope: number;
      }
    | {
        type: 'createline';
        length: number;
        planarLength: number;
        slope: number;
      }
    | {
        type: 'face';
        area: number;
        planarArea: number;
        slope: number;
      }
    | {
        type: 'point';
      };
};

export function summarizeMetadata(node: DesignNode, selection: SelectionResult): DesignMetaData {
  const faces = selection.faces.map((x) => node.getFaceById(x));

  const points = selection.points
    .map((x) => node.getPointById(x))
    .filter((x) => x.segments.length === 0);

  const lineSegments = selection.lineSegments
    .map((x) => node.getLineSegmentById(x))
    .filter((x) => getLineSegmentFaces(node, x.id).length === 0);

  return {
    area: sum(faces, (x) => x.area),
    planarArea: sum(faces, (x) => x.planarArea),
    lineLength: sum(lineSegments, (x) => x.length),
    linePlanarLength: sum(lineSegments, (x) => x.planarLength),
    triangleCount: faces.length,
    lineSegmentCount: lineSegments.length,
    pointCount: points.length,
  };
}

export function summarizeHoverMetadata(tool: DesignTool): DesignHoverMetadata | undefined {
  const faceId = findFaceIdFromIntersection(tool.asset.designNode, tool.intersection);
  const lineSegmentId = findLineSegmentIdFromIntersection(tool.asset.designNode, tool.intersection);
  const pointId = findPointIdFromIntersection(tool.asset.designNode, tool.intersection);

  // A curve has an origin until its end is placed, and then the curve is bent instead.
  if (
    tool.currentPoint &&
    (tool.actionInProgress?.actionName === ActionName.CreateLine ||
      tool.actionInProgress?.actionName === ActionName.CreateCurve) &&
    tool.actionInProgress.origin
  ) {
    return {
      data: {
        type: 'createline',
        length: tool.currentPoint.distanceTo(tool.actionInProgress.origin),
        planarLength: tool.currentPoint
          .clone()
          .setZ(tool.actionInProgress.origin.z)
          .distanceTo(tool.actionInProgress.origin),
        slope: vectorToSlopePercent(tool.currentPoint.clone().sub(tool.actionInProgress.origin)),
      },
      attributes: [],
    };
  }

  if (isDefined(pointId)) {
    const point = tool.asset.designNode.getPointById(pointId);

    return {
      data: {
        type: 'point',
      },
      attributes: getAttributes(tool.asset, point.attributes),
    };
  }

  if (isDefined(lineSegmentId)) {
    const lineSegment = tool.asset.designNode.getLineSegmentById(lineSegmentId);
    const [a, b] = tool.asset.designNode.getVerticesByLineSegmentId(lineSegmentId, 'local');

    return {
      data: {
        type: 'line',
        length: lineSegment.length,
        planarLength: lineSegment.planarLength,
        // The slope of a curve is along the curve, not along the straight line between its ends.
        slope: isDefined(lineSegment.curve)
          ? ((b.z - a.z) / lineSegment.planarLength) * 100
          : vectorToSlopePercent(b.clone().sub(a)),
      },
      attributes: getAttributes(tool.asset, lineSegment.attributes),
    };
  }

  if (isDefined(faceId)) {
    const face = tool.asset.designNode.getFaceById(faceId);
    const [a, b, c] = tool.asset.designNode.getVerticesByFaceId(faceId, 'local');

    return {
      data: {
        type: 'face',
        area: face.area,
        planarArea: face.planarArea,
        slope: slopePercentFromTriangle(a, b, c),
      },
      attributes: getAttributes(tool.asset, face.attributes),
    };
  }

  return;
}

function getAttributes(
  design: Design,
  attributes: Map<AttributeId, AttributeData>,
): [string, string][] {
  const summary: [string, string][] = [];
  for (const [id, data] of attributes) {
    const attribute = design.designNode.getAttribute(id);
    summary.push([attribute.name, getAttributeValue(attribute, data)]);
  }

  return summary;
}

export function getAttributeValue(attribute: SkyMapAttribute, data: AttributeData): string {
  if (data.type === 'enum' && attribute.type === 'enum') {
    return attribute.valueMap.get(data.index) ?? '';
  }

  if (data.type === 'number' && attribute.type === 'number') {
    if (attribute.unit === getUnitType('timestamp')) {
      return formattedDateTime(data.value);
    }

    if (attribute.unit === getUnitType('Ø')) {
      return `${attribute.unit} ${data.value.toString()}`;
    }

    if (isDefined(attribute.unit) && attribute.unit !== getUnitType('none')) {
      return `${data.value.toString()} ${attribute.unit}`;
    }

    return data.value.toString();
  }

  if (data.type === 'string' && attribute.type === 'string') {
    return data.value.toString();
  }

  return '';
}
